import React, { useEffect, useMemo, useState } from "react";
import "./DistributionTransparencyPanel.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const CLIENT_SAFE_COPY =
  "Aggregated delivery distribution across eligible apps, publishers, and supply sources where available. Some inventory may be grouped or unavailable depending on channel, supply path, and reporting permissions.";
const CLIENT_SAFE_HELP_COPY =
  "Rows are aggregated reporting views. App, Supply Source, and Publisher dimensions may overlap, so totals should not be added together across types.";

function safeBase(base) {
  return String(base || "").replace(/\/+$/, "");
}

function getAuthToken() {
  return (
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function formatInteger(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unavailable";
  return Math.round(numeric).toLocaleString("en-US");
}

function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unavailable";
  return `$${numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unavailable";
  const percent = numeric > 1 ? numeric : numeric * 100;
  return `${percent.toFixed(2)}%`;
}

function normalizePlatformKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function typeLabel(row, platformKey) {
  const inventoryType = String(row?.inventoryType || "").toLowerCase();
  const dimensionType = String(row?.dimensionType || "").toLowerCase();
  const isCtv = normalizePlatformKey(platformKey) === "ctv";

  if (dimensionType === "other" || inventoryType === "other") return "Grouped Inventory";
  if (
    inventoryType === "ctv_publisher" ||
    dimensionType === "ctv_publisher" ||
    dimensionType === "publisher"
  ) {
    return isCtv ? "CTV Publisher" : "Publisher";
  }
  if (
    inventoryType === "supply_source" ||
    dimensionType === "supply_source" ||
    dimensionType === "supplysource"
  ) {
    return "Supply Source";
  }
  if (inventoryType === "app" || dimensionType === "app") return "App";
  return "Inventory";
}

function inventoryLabel(row) {
  if (String(row?.dimensionType || "").toLowerCase() === "other") return "Other";
  return row?.label || "Inventory source";
}

function hasVideoMetrics(rows = []) {
  return rows.some((row) => Number.isFinite(Number(row?.videoCompletionRate)));
}

function buildQuery({
  advertiserId,
  campaignId,
  platformKey,
  startDate,
  endDate,
  maxRows,
}) {
  const params = new URLSearchParams();
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  if (campaignId) params.set("campaignId", campaignId);
  if (platformKey) params.set("channel", platformKey);
  params.set("breakdowns", "app,supplySource,publisher");
  params.set("limit", String(maxRows || 25));
  params.set("includeOther", "1");
  return `/api/stackadapt/${encodeURIComponent(advertiserId)}/distribution-transparency?${params.toString()}`;
}

async function fetchDistributionTransparency(path, { signal } = {}) {
  const token = getAuthToken();
  const response = await fetch(`${safeBase(API_BASE_URL)}${path}`, {
    signal,
    cache: "no-store",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(
      payload?.message || payload?.error || `Request failed (${response.status})`
    );
    error.payload = payload;
    throw error;
  }
  return payload;
}

export default function DistributionTransparencyPanel({
  advertiserId,
  campaignId = null,
  platformKey,
  platformLabel,
  startDate = null,
  endDate = null,
  mode = "client",
  maxRows = 25,
}) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    payload: null,
  });
  const isSuperadminMode = mode === "superadmin";
  const showSpendColumn = isSuperadminMode;

  const path = useMemo(() => {
    if (!advertiserId) return "";
    return buildQuery({
      advertiserId,
      campaignId,
      platformKey,
      startDate,
      endDate,
      maxRows: isSuperadminMode ? maxRows : Math.min(Number(maxRows) || 25, 10),
    });
  }, [advertiserId, campaignId, endDate, isSuperadminMode, maxRows, platformKey, startDate]);

  useEffect(() => {
    if (!path) {
      setState({ loading: false, error: null, payload: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState((current) => ({ ...current, loading: true, error: null }));

    fetchDistributionTransparency(path, { signal: controller.signal })
      .then((payload) => {
        if (cancelled) return;
        setState({ loading: false, error: null, payload });
      })
      .catch((error) => {
        if (cancelled || controller.signal.aborted) return;
        setState({ loading: false, error, payload: null });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [path]);

  const rows = useMemo(() => {
    const sourceRows =
      state.payload?.rowProbe?.rows ||
      state.payload?.rows ||
      [];
    const boundedRows = Array.isArray(sourceRows) ? sourceRows : [];
    return isSuperadminMode ? boundedRows : boundedRows.slice(0, Math.min(maxRows, 10));
  }, [isSuperadminMode, maxRows, state.payload]);
  const showVideoColumn = hasVideoMetrics(rows);
  const warnings = Array.isArray(state.payload?.warnings) ? state.payload.warnings : [];

  return (
    <section className="distribution-transparency-panel" data-testid="distribution-transparency-panel">
      <div className="distribution-transparency-header">
        <div>
          <h3>Distribution Transparency</h3>
          <p>{CLIENT_SAFE_COPY}</p>
        </div>
        {isSuperadminMode ? (
          <span className="distribution-transparency-mode">Internal validation</span>
        ) : null}
      </div>

      {state.loading ? (
        <div className="distribution-transparency-empty">Loading distribution transparency.</div>
      ) : state.error ? (
        <div className="distribution-transparency-empty">
          Distribution transparency is not available for this channel and timeframe yet. When
          eligible app, publisher, or supply-source delivery is available, this section will show
          where measured delivery was distributed.
        </div>
      ) : rows.length ? (
        <>
          <div className="distribution-transparency-table-wrap">
            <table className="distribution-transparency-table">
              <thead>
                <tr>
                  <th>Inventory Source</th>
                  <th>Type</th>
                  <th>Impressions</th>
                  <th>Clicks</th>
                  <th>CTR</th>
                  {showSpendColumn ? <th>Spend</th> : null}
                  <th>eCPM</th>
                  <th>Actions</th>
                  {showVideoColumn ? <th>Video Completion Rate</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.dimensionType || "row"}-${row.label || index}-${index}`}>
                    <td>{inventoryLabel(row)}</td>
                    <td>{typeLabel(row, platformKey)}</td>
                    <td>{formatInteger(row.impressions)}</td>
                    <td>{formatInteger(row.clicks)}</td>
                    <td>{formatPercent(row.ctr)}</td>
                    {showSpendColumn ? <td>{formatCurrency(row.spend)}</td> : null}
                    <td>{formatCurrency(row.cpm)}</td>
                    <td>{formatInteger(row.conversions)}</td>
                    {showVideoColumn ? <td>{formatPercent(row.videoCompletionRate)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="distribution-transparency-note">{CLIENT_SAFE_HELP_COPY}</p>
        </>
      ) : (
        <div className="distribution-transparency-empty">
          Distribution transparency is not available for this channel and timeframe yet. When
          eligible app, publisher, or supply-source delivery is available, this section will show
          where measured delivery was distributed.
        </div>
      )}

      {isSuperadminMode ? (
        <div className="distribution-transparency-diagnostics" data-testid="distribution-transparency-diagnostics">
          <span>Provider: StackAdapt</span>
          <span>Rows returned: {rows.length}</span>
          <span>Source granularity: {state.payload?.sourceGranularity || "unknown"}</span>
          {warnings.length ? (
            <span>Warnings: {warnings.map((warning) => warning.code || warning.message).join(", ")}</span>
          ) : null}
          <span>Channel: {platformLabel || platformKey || "Programmatic"}</span>
        </div>
      ) : null}
    </section>
  );
}
